import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  batchDeleteStudents,
  getIndividualStudentResults,
  searchTeacherStudents,
  swapAssessmentStudents,
  transferAssessmentToStudent
} from "../services/APIClient";
import { formatClassDisplayName } from "../utils/classDisplay";
import "./TeacherFixDuplicateChildModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;
const RESULT_DECISIONS = {
  transfer: "transfer",
  keepCurrent: "keepCurrent",
  useDuplicate: "useDuplicate",
  deleteWithDuplicate: "deleteWithDuplicate"
};

const normaliseName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  studentName: String(prefill?.studentName || prefill?.student_name || "").trim(),
  studentClassName: String(
    prefill?.studentClassName ||
    prefill?.student_class_name ||
    prefill?.className ||
    prefill?.class_name ||
    ""
  ).trim(),
  secondStudentName: String(
    prefill?.secondStudentName ||
    prefill?.second_student_name ||
    prefill?.duplicateStudentName ||
    prefill?.duplicate_student_name ||
    ""
  ).trim(),
  secondStudentClassName: String(
    prefill?.secondStudentClassName ||
    prefill?.second_student_class_name ||
    ""
  ).trim(),
  className: String(prefill?.className || prefill?.class_name || "").trim(),
  assessmentName: String(prefill?.assessmentName || prefill?.assessment_name || "").trim()
});

const normaliseChild = (child, countryGrades) => ({
  ...child,
  id: child?.id || child?._id || child?.studentid || child?.studentId,
  name: child?.name || child?.fullName || child?.displayName || "Unnamed child",
  classId: child?.classId || child?.classid || child?.class_id,
  className: child?.className || child?.class_name || "Class unavailable",
  classDisplayName: formatClassDisplayName(child, "Class unavailable", countryGrades),
  academicYear: child?.academicYear || child?.academic_year || "",
  grade: child?.grade || child?.gradeName || child?.grade_name || ""
});

const getResultId = (result) =>
  result?.id ||
  result?._id ||
  result?.assessmentInstanceId ||
  result?.assessment_instance_id ||
  result?.answerid ||
  result?.answerId;

const getResultAssessmentInstanceId = (result) =>
  result?.assessmentInstanceId ||
  result?.assessment_instance_id ||
  result?.assessment_instanceid ||
  result?.assessmentId ||
  result?.assessment_id ||
  result?.id ||
  result?._id ||
  "";

const getResultAssessmentId = (result) =>
  result?.assessmentId ||
  result?.assessmentid ||
  result?.assessment_id ||
  result?.screenerId ||
  result?.screenerid ||
  result?.screener_id ||
  result?.assessmentTypeId ||
  result?.assessment_type_id ||
  "";

const getResultName = (result) =>
  result?.name ||
  result?.assessmentName ||
  result?.screenerName ||
  result?.title ||
  "Screener result";

const normaliseResult = (result) => ({
  ...result,
  id: getResultId(result),
  name: getResultName(result),
  assessmentInstanceId: getResultAssessmentInstanceId(result),
  assessmentId: getResultAssessmentId(result)
});

const getResultMatchKey = (result) => {
  const assessmentId = normaliseName(result?.assessmentId);
  return assessmentId || normaliseName(result?.name);
};

const getRelevantResult = (results, assessmentName) => {
  const normalisedResults = (Array.isArray(results) ? results : [])
    .map(normaliseResult)
    .filter((result) => result.id || result.name);
  const targetName = normaliseName(assessmentName);

  if (targetName) {
    return normalisedResults.find((result) => normaliseName(result.name) === targetName) || null;
  }

  return normalisedResults[0] || null;
};

const classMatchesPrefill = (child, className) => {
  const targetClass = normaliseName(className);

  if (!targetClass) {
    return true;
  }

  return (
    normaliseName(child.className) === targetClass ||
    normaliseName(child.classDisplayName).includes(targetClass)
  );
};

const resultCountLabel = (child) => {
  if (child.resultCheckFailed) {
    return "Could not check results";
  }

  const count = child.results?.length || 0;

  if (count === 0) {
    return "No results";
  }

  return count === 1 ? "1 result" : `${count} results`;
};

const hasResults = (child) => (child?.results?.length || 0) > 0;

const getDuplicateFixErrorMessage = (error) => {
  const status = error?.response?.status;

  if (status === 400) {
    return "Some required details are missing or invalid. Check the selected children and try again.";
  }

  if (status === 401 || status === 403) {
    return "You do not have access to fix this duplicate child.";
  }

  if (status === 404) {
    return "We could not find one of the selected children. Refresh and choose them again.";
  }

  if (status === 409) {
    return "These child records cannot be merged automatically. Review the selected children and results.";
  }

  if (status === 422) {
    return "The duplicate child could not be fixed with these details. Check the selected children and try again.";
  }

  return "The duplicate child could not be fixed. Try again in a moment.";
};

const getDeleteDuplicateReason = (keptChild, duplicateChild) =>
  [
    "Duplicate child record removed",
    keptChild?.name ? `kept ${keptChild.name}` : "",
    keptChild?.id ? `kept child id ${keptChild.id}` : "",
    duplicateChild?.name ? `removed ${duplicateChild.name}` : "",
    duplicateChild?.id ? `removed child id ${duplicateChild.id}` : ""
  ].filter(Boolean).join("; ");

const isDeleteConfirmed = (response, duplicateChildId) => {
  if (!response || response.deletedCount < 1) {
    return false;
  }

  if (Array.isArray(response.deletedStudentIds) && response.deletedStudentIds.length > 0) {
    return response.deletedStudentIds.includes(duplicateChildId);
  }

  return true;
};

const findClearPrefillMatch = (children, childName, className) => {
  const targetName = normaliseName(childName);

  if (!targetName) {
    return null;
  }

  const exactMatches = children.filter(
    (child) => normaliseName(child.name) === targetName && classMatchesPrefill(child, className)
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

const findSecondPrefillMatch = (children, selectedChild, prefill) => {
  if (!selectedChild) {
    return null;
  }

  const candidateChildren = children.filter((child) => child.id !== selectedChild.id);

  const prefilledSecondName = normaliseName(prefill.secondStudentName);

  if (!prefilledSecondName) {
    return null;
  }

  const exactSecondMatches = candidateChildren.filter(
    (child) =>
      normaliseName(child.name) === prefilledSecondName &&
      classMatchesPrefill(child, prefill.secondStudentClassName)
  );

  return exactSecondMatches.length === 1 ? exactSecondMatches[0] : null;
};

const buildResultActionPlan = (keepChild, deleteChild) => {
  const keepResults = Array.isArray(keepChild?.results) ? keepChild.results : [];
  const deleteResults = Array.isArray(deleteChild?.results) ? deleteChild.results : [];

  return deleteResults.map((deleteResult, index) => {
    const key = deleteResult.assessmentInstanceId || deleteResult.id || `${deleteChild?.id}-result-${index}`;
    const matchKey = getResultMatchKey(deleteResult);
    const matchingKeepResult = keepResults.find((keepResult) => getResultMatchKey(keepResult) === matchKey) || null;
    const canTransfer = Boolean(deleteResult.assessmentInstanceId);
    const hasConflict = Boolean(matchingKeepResult);
    const canSwap = Boolean(canTransfer && matchingKeepResult?.assessmentInstanceId);

    return {
      key,
      deleteResult,
      matchingKeepResult,
      canTransfer,
      hasConflict,
      canSwap,
      defaultDecision: hasConflict
        ? ""
        : canTransfer
          ? RESULT_DECISIONS.transfer
          : "",
      requiredDecisions: hasConflict
        ? canSwap
          ? [RESULT_DECISIONS.keepCurrent, RESULT_DECISIONS.useDuplicate]
          : [RESULT_DECISIONS.keepCurrent]
        : canTransfer
          ? [RESULT_DECISIONS.transfer, RESULT_DECISIONS.deleteWithDuplicate]
          : [RESULT_DECISIONS.deleteWithDuplicate]
    };
  });
};

const isResultDecisionComplete = (item, decision) =>
  item.requiredDecisions.includes(decision);

const getResultActionSummary = (item, decision) => {
  if (decision === RESULT_DECISIONS.transfer) {
    return `${item.deleteResult.name} transferred to kept child`;
  }

  if (decision === RESULT_DECISIONS.useDuplicate) {
    return `${item.deleteResult.name} kept by swapping with current result`;
  }

  return `${item.deleteResult.name} deleted with duplicate child`;
};

function TeacherFixDuplicateChildModal({
  show,
  handleClose,
  schoolid = null,
  countryGrades = null,
  teacherid = null,
  teacherName = "",
  prefill = null,
  onViewStudentResults = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("find");
  const [primarySearchTerm, setPrimarySearchTerm] = useState("");
  const [otherSearchTerm, setOtherSearchTerm] = useState("");
  const [primaryMatches, setPrimaryMatches] = useState([]);
  const [otherMatches, setOtherMatches] = useState([]);
  const [selectedPrimaryChild, setSelectedPrimaryChild] = useState(null);
  const [selectedOtherChild, setSelectedOtherChild] = useState(null);
  const [isSearchingPrimary, setIsSearchingPrimary] = useState(false);
  const [isSearchingOther, setIsSearchingOther] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resultDecisions, setResultDecisions] = useState({});
  const [resultActionsCompleted, setResultActionsCompleted] = useState(false);
  const [completedResultActions, setCompletedResultActions] = useState([]);
  const [deletionRetryRequired, setDeletionRetryRequired] = useState(false);
  const prefillAppliedRef = useRef({ primary: false, other: false });
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const trimmedPrimarySearchTerm = primarySearchTerm.trim();
  const trimmedOtherSearchTerm = otherSearchTerm.trim();
  const secondPrefillMatch = useMemo(
    () => findSecondPrefillMatch(primaryMatches, selectedPrimaryChild, prefillContext),
    [primaryMatches, selectedPrimaryChild, prefillContext]
  );
  const otherPrefillMatch = useMemo(
    () => findSecondPrefillMatch(otherMatches, selectedPrimaryChild, prefillContext),
    [otherMatches, selectedPrimaryChild, prefillContext]
  );
  const shouldSearchOtherPrefill = Boolean(
    show &&
    selectedPrimaryChild &&
    !selectedOtherChild &&
    prefillContext.secondStudentName &&
    !secondPrefillMatch
  );
  const selectedPair = Boolean(selectedPrimaryChild && selectedOtherChild);
  const resultActionPlan = useMemo(
    () => buildResultActionPlan(selectedPrimaryChild, selectedOtherChild),
    [selectedPrimaryChild, selectedOtherChild]
  );
  const hasDeleteChildResults = resultActionPlan.length > 0;
  const resultDecisionsComplete = resultActionPlan.every((item) =>
    isResultDecisionComplete(item, resultDecisions[item.key])
  );
  const canCompleteReview = !hasDeleteChildResults || resultDecisionsComplete || resultActionsCompleted;

  const resetForm = useCallback(() => {
    setStage("find");
    setPrimarySearchTerm(prefillContext.studentName);
    setOtherSearchTerm(prefillContext.secondStudentName);
    setPrimaryMatches([]);
    setOtherMatches([]);
    setSelectedPrimaryChild(null);
    setSelectedOtherChild(null);
    setIsSubmitting(false);
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
    prefillAppliedRef.current = { primary: false, other: false };
  }, [prefillContext.studentName, prefillContext.secondStudentName]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  const enrichChildrenWithResults = useCallback(async (children) =>
    Promise.all(
      (Array.isArray(children) ? children : [])
        .map((child) => normaliseChild(child, countryGrades))
        .filter((child) => child.id)
        .map(async (child) => {
          try {
            const results = await getIndividualStudentResults(child.id);
            const normalisedResults = (Array.isArray(results) ? results : [])
              .map(normaliseResult)
              .filter((result) => result.id || result.name);

            return {
              ...child,
              results: normalisedResults,
              relevantResult: getRelevantResult(normalisedResults, prefillContext.assessmentName)
            };
          } catch (resultError) {
            console.error("Error checking duplicate child results:", resultError);

            return {
              ...child,
              results: [],
              relevantResult: null,
              resultCheckFailed: true
            };
          }
        })
    ), [prefillContext.assessmentName, countryGrades]);

  const runSearch = useCallback((term, setMatches, setSearching, failureMessage) => {
    if (!show || term.length < STUDENT_SEARCH_MIN_LENGTH) {
      setMatches([]);
      setSearching(false);
      return undefined;
    }

    let isCurrentSearch = true;
    setSearching(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(term, STUDENT_SEARCH_LIMIT)
        .then(enrichChildrenWithResults)
        .then((children) => {
          if (isCurrentSearch) {
            setMatches(children);
          }
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching duplicate children:", searchError);
          setMatches([]);
          setError(failureMessage);
        })
        .finally(() => {
          if (isCurrentSearch) {
            setSearching(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, enrichChildrenWithResults]);

  useEffect(() =>
    runSearch(
      trimmedPrimarySearchTerm,
      setPrimaryMatches,
      setIsSearchingPrimary,
      "Child search could not be loaded. Please try again."
    ), [runSearch, trimmedPrimarySearchTerm]);

  useEffect(() => {
    if (stage !== "manual" && !shouldSearchOtherPrefill) {
      return undefined;
    }

    return runSearch(
      trimmedOtherSearchTerm,
      setOtherMatches,
      setIsSearchingOther,
      "Other child search could not be loaded. Please try again."
    );
  }, [runSearch, shouldSearchOtherPrefill, stage, trimmedOtherSearchTerm]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.primary ||
      selectedPrimaryChild ||
      primaryMatches.length < 1
    ) {
      return;
    }

    prefillAppliedRef.current.primary = true;
    const matchedChild = findClearPrefillMatch(
      primaryMatches,
      prefillContext.studentName,
      prefillContext.studentClassName
    );

    if (matchedChild) {
      setSelectedPrimaryChild(matchedChild);
    }
  }, [
    show,
    primaryMatches,
    selectedPrimaryChild,
    prefillContext.studentName,
    prefillContext.studentClassName
  ]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.other ||
      selectedOtherChild ||
      !selectedPrimaryChild ||
      (!secondPrefillMatch && !otherPrefillMatch)
    ) {
      return;
    }

    const matchedChild = secondPrefillMatch || otherPrefillMatch;
    prefillAppliedRef.current.other = true;
    setSelectedOtherChild(matchedChild);

    if (!secondPrefillMatch && otherPrefillMatch) {
      setStage("manual");
    }
  }, [
    show,
    selectedOtherChild,
    selectedPrimaryChild,
    secondPrefillMatch,
    otherPrefillMatch
  ]);

  useEffect(() => {
    if (!selectedPair || resultActionsCompleted) {
      return;
    }

    setResultDecisions((currentDecisions) => {
      const nextDecisions = {};
      let changed = false;

      resultActionPlan.forEach((item) => {
        const existingDecision = currentDecisions[item.key];
        const nextDecision = item.requiredDecisions.includes(existingDecision)
          ? existingDecision
          : item.defaultDecision;

        if (nextDecision) {
          nextDecisions[item.key] = nextDecision;
        }

        if (currentDecisions[item.key] !== nextDecisions[item.key]) {
          changed = true;
        }
      });

      if (Object.keys(currentDecisions).length !== Object.keys(nextDecisions).length) {
        changed = true;
      }

      return changed ? nextDecisions : currentDecisions;
    });
  }, [selectedPair, resultActionPlan, resultActionsCompleted]);

  const handleKeepSelect = (child) => {
    setSelectedPrimaryChild(child);
    if (selectedOtherChild?.id === child.id) {
      setSelectedOtherChild(null);
    }
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
  };

  const handlePrimaryChange = () => {
    setSelectedPrimaryChild(null);
    setSelectedOtherChild(null);
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
  };

  const handleDuplicateChange = () => {
    setSelectedOtherChild(null);
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
  };

  const handleDuplicateSelect = (child) => {
    if (
      child.id === selectedPrimaryChild?.id
    ) {
      return;
    }

    setSelectedOtherChild(child);
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
  };

  const handleSwitchResultChoice = () => {
    setSelectedPrimaryChild(selectedOtherChild);
    setSelectedOtherChild(selectedPrimaryChild);
    setError("");
    setResultDecisions({});
    setResultActionsCompleted(false);
    setCompletedResultActions([]);
    setDeletionRetryRequired(false);
  };

  const updateResultDecision = (key, decision) => {
    setResultDecisions((currentDecisions) => ({
      ...currentDecisions,
      [key]: decision
    }));
    setError("");
  };

  const applyResultActions = async () => {
    const completedActions = [];

    for (const item of resultActionPlan) {
      const decision = resultDecisions[item.key];

      if (decision === RESULT_DECISIONS.transfer) {
        await transferAssessmentToStudent(schoolid, item.deleteResult.assessmentInstanceId, selectedPrimaryChild.id);
        completedActions.push(getResultActionSummary(item, decision));
      } else if (decision === RESULT_DECISIONS.useDuplicate) {
        await swapAssessmentStudents(
          schoolid,
          item.deleteResult.assessmentInstanceId,
          item.matchingKeepResult.assessmentInstanceId
        );
        completedActions.push(getResultActionSummary(item, decision));
      } else {
        completedActions.push(getResultActionSummary(item, decision));
      }
    }

    setCompletedResultActions(completedActions);
    setResultActionsCompleted(true);
  };

  const deleteDuplicateChild = async () => {
    const response = await batchDeleteStudents({
      studentIds: [selectedOtherChild.id],
      deletionReason: getDeleteDuplicateReason(selectedPrimaryChild, selectedOtherChild),
      deletedByTeacherId: teacherid?.toString() || "unknown",
      deletedByTeacherName: teacherName || "Unknown Teacher"
    });

    if (!isDeleteConfirmed(response, selectedOtherChild.id)) {
      throw new Error("Duplicate child deletion was not confirmed.");
    }

    return response;
  };

  const handleComplete = async () => {
    if (!selectedPair || isSubmitting) {
      return;
    }

    if (hasDeleteChildResults && stage !== "reviewResults") {
      setStage("reviewResults");
      setError("");
      return;
    }

    if (hasDeleteChildResults && !canCompleteReview) {
      setError("Choose what should happen to each result before deleting the duplicate child.");
      return;
    }

    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      let resultActionsWereHandled = resultActionsCompleted;

      if (hasDeleteChildResults && !resultActionsWereHandled) {
        try {
          await applyResultActions();
          resultActionsWereHandled = true;
        } catch (resultActionError) {
          console.error("Error handling duplicate child results:", resultActionError);
          setError("Results could not be transferred or swapped. The duplicate child was not deleted.");
          return;
        }
      }

      try {
        await deleteDuplicateChild();
      } catch (deleteError) {
        console.error("Error deleting duplicate child:", deleteError);
        setDeletionRetryRequired(hasDeleteChildResults && resultActionsWereHandled);
        setStage(hasDeleteChildResults ? "reviewResults" : stage);
        setError(
          hasDeleteChildResults && resultActionsWereHandled
            ? "Results were handled, but the duplicate child was not deleted. Retry the delete step."
            : "The duplicate child was not deleted. Please try again."
        );
        return;
      }

      setDeletionRetryRequired(false);
      setStage("success");
    } catch (submitError) {
      setError(getDuplicateFixErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: "",
        message: "Duplicate child fixed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderFoldedSelection = (child, label, onChange) => (
    <button
      type="button"
      className="fix-duplicate-child-selected-card"
      onClick={onChange}
      aria-label={`Change ${label.toLowerCase()} ${child.name}`}
    >
      <span className="fix-duplicate-child-result-copy">
        <span className="fix-duplicate-child-result-label">{label}</span>
        <strong>{child.name}</strong>
        <span>{child.classDisplayName || child.className}</span>
        <span>{resultCountLabel(child)}</span>
      </span>
      <span className="fix-duplicate-child-selected-action">Change</span>
    </button>
  );

  const renderSearchResults = ({
    matches,
    isSearching,
    trimmedTerm,
    selectedChild,
    type
  }) => {
    if (isSearching) {
      return (
        <div className="fix-duplicate-child-search-state">
          <Spinner animation="border" size="sm" />
          <span>Searching children...</span>
        </div>
      );
    }

    if (trimmedTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return <p className="fix-duplicate-child-muted">Start typing the name the teacher can see.</p>;
    }

    if (matches.length < 1) {
      return (
        <div className="fix-duplicate-child-empty">
          <strong>No matching children found.</strong>
          <span>Try another name, nickname, or spelling.</span>
        </div>
      );
    }

    const visibleMatches =
      type === "primary" && selectedPrimaryChild
        ? matches.filter((child) => child.id !== selectedPrimaryChild.id)
        : matches;

    if (visibleMatches.length < 1) {
      return (
        <p className="fix-duplicate-child-muted">
          {type === "primary" ? "No other matching children found." : "No other child selected."}
        </p>
      );
    }

    return visibleMatches.map((child) => {
      const isKeepSelection = selectedPrimaryChild?.id === child.id;
      const isDuplicateSelection = selectedOtherChild?.id === child.id;
      const isSelected = selectedChild?.id === child.id || isKeepSelection || isDuplicateSelection;
      const isUnavailable = type === "other" && child.id === selectedPrimaryChild?.id;
      const label = isKeepSelection ? "Keep" : isDuplicateSelection ? "Delete" : "";

      return (
        <div
          key={`${type}-${child.id}`}
          className={`fix-duplicate-child-result${isSelected ? " selected" : ""}${isUnavailable ? " disabled" : ""}`}
        >
          {label && <span className="fix-duplicate-child-result-label">{label}</span>}
          <strong>{child.name}</strong>
          <span className="fix-duplicate-child-row">
            <span>Class</span>
            <span>{child.classDisplayName || child.className}</span>
          </span>
          <span className="fix-duplicate-child-row">
            <span>Results</span>
            <span>{resultCountLabel(child)}</span>
          </span>
          {type === "primary" ? (
            <span className="fix-duplicate-child-actions">
              <Button
                type="button"
                variant={isKeepSelection ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => handleKeepSelect(child)}
                aria-label={`Keep ${child.name} from ${child.className}`}
              >
                Keep
              </Button>
              <Button
                type="button"
                variant={isDuplicateSelection ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => handleDuplicateSelect(child)}
                aria-label={`Delete duplicate ${child.name}`}
                disabled={!selectedPrimaryChild || child.id === selectedPrimaryChild.id}
              >
                Delete
              </Button>
            </span>
          ) : (
            <Button
              type="button"
              variant={isSelected ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handleDuplicateSelect(child)}
              aria-label={`${isUnavailable ? "Unavailable" : "Select"} ${child.name} from ${child.className}. ${resultCountLabel(child)}`}
              disabled={isUnavailable}
            >
              Select
            </Button>
          )}
        </div>
      );
    });
  };

  const renderResultList = (child) => {
    if (child?.resultCheckFailed) {
      return <p className="fix-duplicate-child-muted">Results could not be checked.</p>;
    }

    if (!hasResults(child)) {
      return <p className="fix-duplicate-child-muted">No results.</p>;
    }

    return (
      <ul className="fix-duplicate-child-result-list">
        {child.results.map((result, index) => (
          <li key={result.id || `${child.id}-result-${index}`}>
            {result.name}
          </li>
        ))}
      </ul>
    );
  };

  const renderResultsReviewCard = (child, label, description, isDuplicate = false) => (
    <div className="fix-duplicate-child-results-card">
      <span className={`fix-duplicate-child-result-label${isDuplicate ? " remove" : ""}`}>{label}</span>
      <strong>{child?.name}</strong>
      <span className="fix-duplicate-child-row">
        <span>Class</span>
        <span>{child?.classDisplayName || child?.className}</span>
      </span>
      <span className="fix-duplicate-child-results-decision">{description}</span>
      {renderResultList(child)}
    </div>
  );

  const renderResultMeta = (result) => (
    <span className="fix-duplicate-child-result-meta">
      {result?.assessmentInstanceId ? `Assessment instance: ${result.assessmentInstanceId}` : "No assessment instance id available"}
    </span>
  );

  const renderDecisionOption = (item, decision, label, description, disabled = false) => (
    <Form.Check
      key={`${item.key}-${decision}`}
      type="radio"
      id={`duplicate-child-${item.key}-${decision}`}
      name={`duplicate-child-result-${item.key}`}
      label={
        <span className="fix-duplicate-child-decision-copy">
          <strong>{label}</strong>
          <span>{description}</span>
        </span>
      }
      checked={resultDecisions[item.key] === decision}
      onChange={() => updateResultDecision(item.key, decision)}
      disabled={disabled || isSubmitting || resultActionsCompleted}
      className="fix-duplicate-child-decision-option"
    />
  );

  const renderResultActionItem = (item) => {
    const decision = resultDecisions[item.key];

    return (
      <div key={item.key} className="fix-duplicate-child-action-card">
        <div className="fix-duplicate-child-action-heading">
          <span className={`fix-duplicate-child-result-label${item.hasConflict ? " warning" : ""}`}>
            {item.hasConflict ? "Conflict" : item.canTransfer ? "Transfer" : "Cannot transfer"}
          </span>
          <strong>{item.deleteResult.name}</strong>
          {renderResultMeta(item.deleteResult)}
        </div>

        {item.hasConflict && item.matchingKeepResult && (
          <div className="fix-duplicate-child-conflict-grid">
            <div>
              <span className="fix-duplicate-child-result-label">Current</span>
              <strong>{item.matchingKeepResult.name}</strong>
              {renderResultMeta(item.matchingKeepResult)}
            </div>
            <div>
              <span className="fix-duplicate-child-result-label remove">Delete child</span>
              <strong>{item.deleteResult.name}</strong>
              {renderResultMeta(item.deleteResult)}
            </div>
          </div>
        )}

        <div className="fix-duplicate-child-decision-list">
          {item.hasConflict ? (
            <>
              {renderDecisionOption(
                item,
                RESULT_DECISIONS.keepCurrent,
                "Keep current result",
                "The kept child keeps its current result. The duplicate child's matching result is deleted with the duplicate child."
              )}
              {item.canSwap ? renderDecisionOption(
                item,
                RESULT_DECISIONS.useDuplicate,
                "Use duplicate child result",
                "Swap the two matching results first, then delete the duplicate child."
              ) : (
                <p className="fix-duplicate-child-muted">
                  The duplicate child result cannot replace the current result because one assessment instance id is missing.
                </p>
              )}
            </>
          ) : item.canTransfer ? (
            <>
              {renderDecisionOption(
                item,
                RESULT_DECISIONS.transfer,
                "Transfer to kept child",
                "Move this result to the kept child before deleting the duplicate child."
              )}
              {renderDecisionOption(
                item,
                RESULT_DECISIONS.deleteWithDuplicate,
                "Delete with duplicate child",
                "Do not transfer this result. It will be removed with the duplicate child."
              )}
            </>
          ) : (
            <>
              <p className="fix-duplicate-child-muted">
                This result cannot be transferred because it has no assessment instance id.
              </p>
              {renderDecisionOption(
                item,
                RESULT_DECISIONS.deleteWithDuplicate,
                "Delete with duplicate child",
                "Confirm this result should be removed with the duplicate child."
              )}
            </>
          )}
        </div>

        {!isResultDecisionComplete(item, decision) && (
          <p className="fix-duplicate-child-decision-required">Choose an action for this result.</p>
        )}
      </div>
    );
  };

  const renderFind = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Fix Duplicate Child</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-child-intro">
          Find the child who is showing more than once.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="teacherFixDuplicatePrimarySearch">
          <Form.Label>Which child appears twice?</Form.Label>
          <Form.Text as="div" className="fix-duplicate-child-field-label">
            Child name
          </Form.Text>
          <Form.Control
            type="search"
            value={primarySearchTerm}
            onChange={(event) => {
              setPrimarySearchTerm(event.target.value);
              setSelectedPrimaryChild(null);
              setSelectedOtherChild(null);
              setError("");
            }}
            aria-label="Search duplicate child"
          />
        </Form.Group>
        <div className="fix-duplicate-child-results" aria-label="Matching duplicate children">
          {selectedPrimaryChild && renderFoldedSelection(selectedPrimaryChild, "Keep", handlePrimaryChange)}
          {selectedOtherChild && renderFoldedSelection(selectedOtherChild, "Delete", handleDuplicateChange)}
          {(!selectedPrimaryChild || !selectedOtherChild) &&
            renderSearchResults({
              matches: primaryMatches,
              isSearching: isSearchingPrimary,
              trimmedTerm: trimmedPrimarySearchTerm,
              selectedChild: selectedPrimaryChild,
              type: "primary"
            })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-duplicate-child-footer-note">
          {selectedPair
            ? `${selectedPrimaryChild.name} and ${selectedOtherChild.name} selected.`
            : selectedPrimaryChild
              ? "Choose the child to delete."
              : "Choose the child to keep first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => setStage("manual")}
          disabled={!selectedPrimaryChild || selectedPair}
        >
          Find a child with another name
        </Button>
        <Button variant="primary" onClick={handleComplete} disabled={!selectedPair || isSubmitting}>
          {isSubmitting ? "Fixing..." : "Complete"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderManual = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Find a Child With Another Name</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-child-intro">No close match was suggested automatically.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="fix-duplicate-child-manual-grid">
          {renderFoldedSelection(selectedPrimaryChild, "Keep", () => {
            handlePrimaryChange();
            setStage("find");
          })}
          <div>
            <Form.Group controlId="teacherFixDuplicateOtherSearch">
              <Form.Label>What is the other name?</Form.Label>
              <Form.Text as="div" className="fix-duplicate-child-field-label">
                Search another child
              </Form.Text>
              <Form.Control
                type="search"
                value={otherSearchTerm}
                onChange={(event) => {
                  setOtherSearchTerm(event.target.value);
                  setSelectedOtherChild(null);
                  setError("");
                }}
                aria-label="Search other duplicate child"
              />
            </Form.Group>
            <div className="fix-duplicate-child-results" aria-label="Matching other children">
              {selectedOtherChild
                ? renderFoldedSelection(selectedOtherChild, "Delete", handleDuplicateChange)
                : renderSearchResults({
                    matches: otherMatches,
                    isSearching: isSearchingOther,
                    trimmedTerm: trimmedOtherSearchTerm,
                    selectedChild: selectedOtherChild,
                    type: "other"
                  })}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-duplicate-child-footer-note">
          {selectedOtherChild ? "Manual match selected." : "Choose the other child."}
        </span>
        <Button
          variant="outline-secondary"
          onClick={() => setStage("find")}
        >
          Back
        </Button>
        <Button variant="primary" onClick={handleComplete} disabled={!selectedOtherChild || isSubmitting}>
          {isSubmitting ? "Fixing..." : "Complete"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderReviewResults = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Review Student Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-child-intro">
          Review the duplicate child's results before deleting that child record.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {resultActionsCompleted && completedResultActions.length > 0 && (
          <Alert variant="info" className="fix-duplicate-child-action-summary">
            <strong>Result actions completed.</strong>
            <ul>
              {completedResultActions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="fix-duplicate-child-results-review">
          {renderResultsReviewCard(
            selectedPrimaryChild,
            "Keep",
            "These results will stay with the child."
          )}
          {renderResultsReviewCard(
            selectedOtherChild,
            "Delete",
            "These results will no longer be shown with a separate duplicate child.",
            true
          )}
        </div>
        <div className="fix-duplicate-child-action-plan">
          <h3>Result actions</h3>
          {resultActionPlan.map(renderResultActionItem)}
        </div>
      </Modal.Body>
      <Modal.Footer className="fix-duplicate-child-review-footer">
        <Button
          variant="outline-secondary"
          onClick={() => setStage("find")}
          disabled={isSubmitting || resultActionsCompleted}
        >
          Back
        </Button>
        <Button
          variant="outline-secondary"
          onClick={handleSwitchResultChoice}
          disabled={isSubmitting || resultActionsCompleted}
        >
          Switch keep/delete
        </Button>
        <Button variant="primary" onClick={handleComplete} disabled={isSubmitting || !canCompleteReview}>
          {isSubmitting
            ? "Fixing..."
            : deletionRetryRequired
              ? "Retry delete duplicate child"
              : "Fix duplicate child"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const keptChild = selectedPrimaryChild;
    const duplicateChild = selectedOtherChild;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Duplicate Child Fixed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fix-duplicate-child-intro">The duplicate child correction is complete.</p>
          <Alert variant="success" className="fix-duplicate-child-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>{duplicateChild?.name} is no longer shown as a separate duplicate child.</strong>
              <span>{keptChild?.name} remains as the child to keep.</span>
            </span>
          </Alert>
          <div className="fix-duplicate-child-summary">
            <strong>What happened</strong>
            <span className="fix-duplicate-child-row">
              <span>Kept</span>
              <span>{keptChild?.name}</span>
            </span>
            <span className="fix-duplicate-child-row">
              <span>No longer shown</span>
              <span>{duplicateChild?.name}</span>
            </span>
            <span className="fix-duplicate-child-row">
              <span>Class</span>
              <span>{keptChild?.className || selectedPrimaryChild?.className}</span>
            </span>
            {completedResultActions.map((action) => (
              <span key={action} className="fix-duplicate-child-row">
                <span>Result</span>
                <span>{action}</span>
              </span>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="fix-duplicate-child-footer-note">
            {embedded ? "Done returns to SENSAI." : "Done returns to Help Centre home."}
          </span>
          <Button variant="outline-secondary" onClick={resetForm}>
            Fix another duplicate
          </Button>
          {!embedded && onViewStudentResults && (
            <Button
              variant="outline-secondary"
              onClick={() => onViewStudentResults()}
            >
              View student results
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderStage = () => {
    if (stage === "manual") {
      return renderManual();
    }

    if (stage === "reviewResults") {
      return renderReviewResults();
    }

    if (stage === "success") {
      return renderSuccess();
    }

    return renderFind();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      className={`teacher-fix-duplicate-child-modal${stage === "reviewResults" ? " review-results" : ""}`}
      backdrop="static"
    >
      {renderStage()}
    </Modal>
  );
}

export default TeacherFixDuplicateChildModal;
